import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import {
  LogIn,
  ArrowLeft,
  Shield,
  Eye,
  EyeOff,
  Loader2,
  AlertCircle,
  Settings,
} from "lucide-react";
import { useAuth } from "../../hooks/useAuth";

export function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const navigate = useNavigate();
  const hasCheckedAuth = useRef(false);

  const { login, logout, isLoading, error, isAuthenticated, isAdmin, clearError } = useAuth();

  // Ao montar o componente, verifica e limpa sessão de cliente
  useEffect(() => {
    if (hasCheckedAuth.current) return;
    hasCheckedAuth.current = true;

    if (isAuthenticated) {
      if (isAdmin) {
        // Já é admin, redireciona direto
        navigate("/dashboard", { replace: true });
      } else {
        // É cliente, faz logout para permitir login como admin
        logout();
        setIsReady(true);
      }
    } else {
      setIsReady(true);
    }
  }, [isAuthenticated, isAdmin, logout, navigate]);

  useEffect(() => {
    return () => clearError();
  }, [clearError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);

    try {
      const result = await login(email, password);

      if (result?.user?.role === "admin") {
        // Redireciona para dashboard usando window.location para garantir
        window.location.href = "/dashboard";
      } else {
        // Não é admin, faz logout e mostra erro
        logout();
        setLoginError("Acesso restrito a administradores");
      }
    } catch {
      // Erro já tratado no store
    }
  };

  const displayError = loginError || error;

  // Não renderiza até verificar autenticação
  if (!isReady) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Área Administrativa | Arcgel</title>
        <meta
          name="description"
          content="Acesso restrito para administradores do sistema Arcgel."
        />
      </Helmet>

      <div className="min-h-screen flex items-center justify-center py-20 px-4">
        {/* Background decorativo */}
        <div className="absolute inset-0 z-0">
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-gradient-radial from-indigo-500/10 to-transparent rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-gradient-radial from-purple-500/10 to-transparent rounded-full blur-3xl" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md relative z-10"
        >
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar para Home
          </Link>

          <div className="p-8 rounded-2xl bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 shadow-2xl">
            <div className="text-center mb-8">
              <div className="inline-flex p-3 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 mb-4">
                <Settings className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-white">
                Área Administrativa
              </h1>
              <p className="text-slate-400 text-sm mt-2">
                Acesso restrito para administradores
              </p>
            </div>

            {displayError && (
              <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center gap-2 text-red-400">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{displayError}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label
                  htmlFor="admin-email"
                  className="block text-sm font-medium text-slate-300 mb-2"
                >
                  Email
                </label>
                <input
                  type="email"
                  id="admin-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading}
                  placeholder="admin@arcgel.com.br"
                  className="w-full px-4 py-3 rounded-lg bg-slate-900/50 border border-slate-600 text-white placeholder-slate-500 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors disabled:opacity-50"
                />
              </div>

              <div>
                <label
                  htmlFor="admin-password"
                  className="block text-sm font-medium text-slate-300 mb-2"
                >
                  Senha
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="admin-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    placeholder="••••••••"
                    className="w-full px-4 py-3 rounded-lg bg-slate-900/50 border border-slate-600 text-white placeholder-slate-500 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors disabled:opacity-50 pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 px-4 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold rounded-lg hover:from-indigo-600 hover:to-purple-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Entrando...
                  </>
                ) : (
                  <>
                    Entrar
                    <LogIn className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 pt-6 border-t border-slate-700/50">
              <div className="flex items-center gap-2 text-slate-500 text-xs justify-center">
                <Shield className="w-4 h-4" />
                <span>Conexão segura e criptografada</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
}
